import type { IFilterableListItem, IOmit, ISuggestProps } from '@sixui/ui';
import { forwardRef } from 'react';
import { Field, Skeleton, Suggest } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IOrganizationUserFilter } from './organizationUserFilterPredicate';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { parseUserInfo } from '~/helpers/parseUserInfo';
import { useOrganizationUsersQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUsersQuery';
import { organizationUserFilterPredicate } from './organizationUserFilterPredicate';

export type IOrganizationUserSelectorProps = IOmit<ISuggestProps, 'items'> & {
  organizationId: string;
  filter?: IOrganizationUserFilter;
};

export const OrganizationUserSelector = forwardRef<
  HTMLInputElement,
  IOrganizationUserSelectorProps
>(function OrganizationUserSelector(props, forwardedRef) {
  const { organizationId, filter, ...other } = props;
  const { t } = useTranslation();
  const organizationUsersQuery = useOrganizationUsersQuery({
    organizationId,
  });
  const filteredOrganizationUsers = filter
    ? organizationUsersQuery.data?.items.filter((organizationUser) =>
        organizationUserFilterPredicate(organizationUser, filter),
      )
    : organizationUsersQuery.data?.items;
  const items: Array<IFilterableListItem> =
    filteredOrganizationUsers?.map((organizationUser) => {
      const organizationUserInfo = parseUserInfo(organizationUser);

      return {
        value: organizationUser._id,
        label: organizationUserInfo.fullName,
        supportingText: organizationUserInfo.emailAddress,
      };
    }) ?? [];

  return organizationUsersQuery.isError ? (
    <ErrorMessage error={organizationUsersQuery.error} />
  ) : !!organizationUsersQuery.data || !organizationUsersQuery.isLoading ? (
    <Suggest
      {...other}
      items={items}
      ref={forwardedRef}
      noResultsLabel={t('OrganizationUserSelector.empty')}
    />
  ) : (
    <Skeleton>
      <Field />
    </Skeleton>
  );
});
